/**
 * Device List Panel Component
 * Shows active devices sorted by most recent reports. Row click selects;
 * the follow icon is what locks the camera, so an analyst can inspect a
 * device without the map chasing it.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Chip,
  Link,
  IconButton,
  Tooltip,
  TablePagination,
  useTheme,
  alpha,
  Popover,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Battery5Bar as BatteryIcon,
  Speed as SpeedIcon,
  DirectionsCar,
  PhoneIphone,
  Watch,
  Shield,
  Person,
  LocationOn,
  MyLocation as FollowIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Share as ShareIcon,
  Navigation as NavigationIcon,
  Height as HeightIcon,
  Flight as FlightIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { DeviceLocationStatus } from '../services/copApi';
import { getDeviceColor, COP_DEVICE_COLORS } from '../utils/deviceColors';
import { formatSpeedMph } from '../utils/speed';
import { formatBearing } from '../utils/droneTelemetry';
import {
  COTRAVELER_HELP,
  COTRAVELER_PULSE_COLOR,
  COTRAVELER_TINT_RING,
  COTRAVELER_TINT_ROW,
} from '../utils/cotravelerFollow';
import { formatUtcInstantInTimeZone, getBrowserTimeZone } from '@signals/app-shell/utils/time';

interface DeviceListPanelProps {
  devices: DeviceLocationStatus[];
  selectedDevice: DeviceLocationStatus | null;
  followedDevice?: DeviceLocationStatus | null;
  /** Device ids currently tailed by an open DFP co-traveler alert. */
  cotravelerFollowedIds?: Set<string>;
  /** One follow, not a scanner count — e.g. "1 co-traveler near VigilMortis". */
  cotravelerBanner?: string | null;
  onDeviceSelect: (device: DeviceLocationStatus) => void;
  /** Lock or release the map on this device. Separate from select. */
  onFollowToggle?: (device: DeviceLocationStatus) => void;
  onShareToChat?: (device: DeviceLocationStatus) => void;
  statusFilterLabel?: string | null;
  statusFilterColor?: string | null;
  onClearStatusFilter?: () => void;
  timeZone?: string;
}

const DeviceListPanel: React.FC<DeviceListPanelProps> = ({
  devices,
  selectedDevice,
  followedDevice,
  cotravelerFollowedIds,
  cotravelerBanner,
  onDeviceSelect,
  onFollowToggle,
  onShareToChat,
  statusFilterLabel,
  statusFilterColor,
  onClearStatusFilter,
  timeZone,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  const colors = {
    cardBg: isDark
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? `rgba(${primaryRgb}, 0.25)` : `rgba(${primaryRgb}, 0.3)`,
    headerBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
  };

  type EntityBreakdownRow = {
    total: number;
    active: number;
    color: string;
    deviceTypes: Record<string, { total: number; active: number }>;
  };

  // Entity type + device type breakdown
  const entityBreakdown = useMemo((): Record<string, EntityBreakdownRow> => {
    const types: Record<string, EntityBreakdownRow> = {};
    const typeColors: Record<string, string> = { Student: '#4caf50', RP: '#ff9800', SRP: '#f44336' };

    devices.forEach(d => {
      const et = d.entity_type || 'Unknown';
      if (!types[et]) types[et] = { total: 0, active: 0, color: typeColors[et] || '#888', deviceTypes: {} };
      types[et].total++;
      const isActive = (d.last_seen_minutes_ago ?? 999) < 30;
      if (isActive) types[et].active++;

      if (et === 'Student' && d.device_type) {
        const dt = d.device_type.charAt(0).toUpperCase() + d.device_type.slice(1).toLowerCase();
        if (!types[et].deviceTypes[dt]) types[et].deviceTypes[dt] = { total: 0, active: 0 };
        types[et].deviceTypes[dt].total++;
        if (isActive) types[et].deviceTypes[dt].active++;
      }
    });

    return types;
  }, [devices]);

  const [_lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [, setTick] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  
  useEffect(() => {
    setLastUpdated(new Date());
  }, [devices.length, devices[0]?.timestamp]);

  // Reset to first page when the filtered set changes size (filter / bounds).
  useEffect(() => {
    setPage(0);
  }, [devices.length, statusFilterLabel]);
  
  useEffect(() => {
    const timer = setInterval(() => setTick(prev => prev + 1), 5000);
    return () => clearInterval(timer);
  }, []);
  
  const parseTimestamp = (rawTs: string): number => {
    if (!rawTs) return NaN;
    if (rawTs.includes('Z') || /[+-]\d{2}:\d{2}/.test(rawTs)) {
      return new Date(rawTs).getTime();
    }
    const match = rawTs.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hour, min, sec] = match;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(min), parseInt(sec)).getTime();
    }
    return new Date(rawTs).getTime();
  };

  const getElapsedSeconds = (device: DeviceLocationStatus): number => {
    const rawTs = device.timestamp || '';
    const deviceTimestamp = parseTimestamp(rawTs);
    if (isNaN(deviceTimestamp)) return 999999;
    return Math.max(0, Math.floor((Date.now() - deviceTimestamp) / 1000));
  };
  
  const formatElapsedTime = (device: DeviceLocationStatus): string => {
    const elapsedSeconds = getElapsedSeconds(device);
    const numMinutes = Math.floor(elapsedSeconds / 60);
    const numHours = Math.floor(elapsedSeconds / 3600);
    const numDays = Math.floor(elapsedSeconds / 86400);

    if (numMinutes < 1) return elapsedSeconds <= 5 ? 'Just Now' : `${elapsedSeconds}s ago`;
    if (numHours < 1) return `${numMinutes}m ago`;
    if (numDays < 1) return `${numHours}h ago`;
    return `${numDays}d ago`;
  };

  // Delegates to the shared color util so every COP surface (map, list,
  // legend, stats, alerts, dwells) classifies the same device the same
  // way. Students split by device_type (phone=green / watch=blue /
  // vehicle=purple); RP/SRP keep their role color.
  const getEntityColor = (device: DeviceLocationStatus): string => getDeviceColor(device);

  const getEntityNumber = (device: DeviceLocationStatus): string => {
    const entityId = device.entity_id || '';
    const match = entityId.match(/[-\s](\d+)$/);
    return match ? match[1] : '';
  };

  const getDeviceTypeInfo = (device: DeviceLocationStatus): { prefix: string; icon: React.ReactNode } => {
    const deviceType = device.device_type?.toLowerCase() || '';
    const iconSx = { fontSize: 16, mr: 0.5 };
    if (deviceType.includes('wardriver') || deviceType.includes('beluga')) {
      return { prefix: 'B', icon: <LocationOn sx={iconSx} /> };
    }
    if (deviceType.includes('vehicle')) return { prefix: 'V', icon: <DirectionsCar sx={iconSx} /> };
    if (deviceType.includes('phone')) return { prefix: 'P', icon: <PhoneIphone sx={iconSx} /> };
    if (deviceType.includes('watch')) return { prefix: 'W', icon: <Watch sx={iconSx} /> };
    if (deviceType.includes('drone')) return { prefix: 'D', icon: <FlightIcon sx={iconSx} /> };
    if (deviceType === 'srp') return { prefix: 'SR', icon: <Shield sx={iconSx} /> };
    if (deviceType === 'rp') return { prefix: 'RP', icon: <Person sx={iconSx} /> };
    return { prefix: '', icon: <LocationOn sx={iconSx} /> };
  };

  const getDeviceLabel = (device: DeviceLocationStatus): string => {
    const deviceType = device.device_type?.toLowerCase() || '';
    const entityType = device.entity_type?.toLowerCase() || '';
    const entityId = device.entity_id || '';
    const entityIdLower = entityId.toLowerCase();
    const idTokens = entityIdLower.split(/[-_\s]+/);
    
    // Get the device type name
    const getTypeName = (): string => {
      if (deviceType.includes('wardriver') || deviceType.includes('beluga')) return 'Wardriver';
      if (deviceType.includes('watch')) return 'Watch';
      if (deviceType.includes('phone')) return 'Phone';
      if (deviceType.includes('vehicle')) return 'Vehicle';
      if (deviceType.includes('drone')) return 'Drone';
      if (deviceType === 'srp') return 'SRP';
      if (deviceType === 'rp') return 'RP';
      return '';
    };
    
    const typeName = getTypeName();
    
    // For RP/SRP, use the original format. Check SRP before RP and match RP
    // as a discrete token — "srp" contains "rp" as a substring.
    if (deviceType === 'srp' || entityType === 'srp' || entityIdLower.includes('srp')) {
      const entityNumber = getEntityNumber(device);
      return entityNumber ? `SRP-${entityNumber}` : 'SRP';
    }
    if (deviceType === 'rp' || entityType === 'rp' || idTokens.includes('rp')) {
      const entityNumber = getEntityNumber(device);
      return entityNumber ? `RP-${entityNumber}` : 'RP';
    }
    
    // For regular devices, show entity name + device type
    if (entityId && typeName) {
      return `${entityId} ${typeName}`;
    }
    
    return entityId || `Device-${device.device_id.substring(0, 6)}`;
  };

  const getGoogleStreetViewUrl = (lat: number, lon: number): string => {
    // Format: @lat,lon,3a,75y,90t/data=!3m4!1e1!3m2!1s0:0!2e0
    // 3a = street view, 75y = field of view, 90t = heading
    return `https://www.google.com/maps/@${lat},${lon},3a,75y,90t/data=!3m4!1e1!3m2!1s0:0!2e0`;
  };

  const sortedDevices = useMemo(() => {
    const followedDeviceId = followedDevice?.device_id;
    return [...devices].sort((a, b) => {
      if (followedDeviceId) {
        if (a.device_id === followedDeviceId) return -1;
        if (b.device_id === followedDeviceId) return 1;
      }
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [devices, followedDevice?.device_id]);

  const pageCount = sortedDevices.length;
  const safePage = Math.min(page, Math.max(0, Math.ceil(pageCount / rowsPerPage) - 1 || 0));
  const pagedDevices = useMemo(
    () => sortedDevices.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedDevices, safePage, rowsPerPage],
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header + Entity Breakdown Card */}
      <Paper sx={{ 
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        mb: 2
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${colors.border}`,
          background: colors.headerBg,
        }}>
          <Box sx={{
            width: 28,
            height: 28,
            borderRadius: 2,
            background: isDark 
              ? `linear-gradient(135deg, rgba(${primaryRgb}, 0.2), rgba(${primaryRgb}, 0.1))`
              : `linear-gradient(135deg, rgba(${primaryRgb}, 0.15), rgba(${primaryRgb}, 0.08))`,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <DevicesIcon sx={{ color: primaryColor, fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: colors.text, fontWeight: 'bold', fontSize: '0.8rem' }}>
              Devices in View
            </Typography>
            <Typography sx={{ color: colors.textTertiary, fontSize: '0.6rem' }}>
              {devices.length} devices • Click to follow
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="How FOLLOWED is decided"
            aria-expanded={!!helpAnchor}
            onClick={(e) => {
              e.stopPropagation();
              setHelpAnchor(e.currentTarget);
            }}
            sx={{
              p: 0.35,
              color: colors.textTertiary,
              '&:hover': { color: COTRAVELER_PULSE_COLOR, bgcolor: 'rgba(255,23,68,0.08)' },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Popover
            open={!!helpAnchor}
            anchorEl={helpAnchor}
            onClose={() => setHelpAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  maxWidth: 340,
                  maxHeight: 440,
                  overflow: 'auto',
                  bgcolor: isDark ? '#1b1e26' : '#fff',
                  color: colors.text,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.13)' : colors.border}`,
                  boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
                },
              },
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1.4 }}>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color: COTRAVELER_PULSE_COLOR,
                  mb: 0.6,
                }}
              >
                {COTRAVELER_HELP.title}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.5, color: colors.text }}>
                {COTRAVELER_HELP.lead}
              </Typography>
              {COTRAVELER_HELP.items.map((item) => (
                <Box key={item.label} sx={{ mt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      color: COTRAVELER_PULSE_COLOR,
                      mb: 0.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', lineHeight: 1.45, color: colors.textSecondary }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>
        </Box>
        {cotravelerBanner && (
          <Box sx={{ px: 2, pt: 1 }}>
            <Chip
              label={cotravelerBanner}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: 'rgba(255, 23, 68, 0.15)',
                color: COTRAVELER_PULSE_COLOR,
                border: '1px solid rgba(255, 23, 68, 0.5)',
              }}
            />
          </Box>
        )}
        {statusFilterLabel && (
          <Box sx={{ px: 2, pt: 1 }}>
            <Chip
              icon={<FilterIcon sx={{ fontSize: '0.85rem' }} />}
              label={statusFilterLabel}
              size="small"
              onDelete={onClearStatusFilter}
              deleteIcon={<CloseIcon sx={{ fontSize: '0.85rem' }} />}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: statusFilterColor ? alpha(statusFilterColor, 0.15) : undefined,
                color: statusFilterColor || undefined,
                border: `1px solid ${statusFilterColor || colors.border}`,
                '& .MuiChip-icon': { color: statusFilterColor || undefined },
                '& .MuiChip-deleteIcon': {
                  color: statusFilterColor || undefined,
                  '&:hover': { color: colors.text },
                },
              }}
            />
          </Box>
        )}
        <Box sx={{ px: 2, py: 1.25 }}>
          {(Object.entries(entityBreakdown) as [string, EntityBreakdownRow][])
            .filter(([_, data]) => data.total > 0)
            .sort(([a], [b]) => { const order = ['Student', 'RP', 'SRP']; return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)); })
            .map(([type, data]) => (
              <Box key={type} sx={{ mb: Object.keys(data.deviceTypes).length > 0 ? 0.75 : 0.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: data.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text }}>
                      {type}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: data.active > 0 ? data.color : colors.textTertiary }}>
                    {data.active}/{data.total}
                  </Typography>
                </Box>
                {Object.keys(data.deviceTypes).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1.5, ml: 2, mt: 0.25 }}>
                    {Object.entries(data.deviceTypes).sort(([a], [b]) => a.localeCompare(b)).map(([dt, counts]) => {
                      // Student sub-rows match map pin colors. RP/SRP stay
                      // in their role color (their sub-rows, if any, keep
                      // data.color).
                      const isStudentGroup = type === 'Student';
                      const lower = dt.toLowerCase();
                      const subColor = isStudentGroup
                        ? lower.includes('wardriver') || lower.includes('beluga') ? COP_DEVICE_COLORS.wardriver
                          : lower.includes('vehicle') ? COP_DEVICE_COLORS.vehicle
                          : lower.includes('watch') ? COP_DEVICE_COLORS.watch
                          : lower.includes('phone') ? COP_DEVICE_COLORS.phone
                          : data.color
                        : data.color;
                      return (
                        <Typography key={dt} sx={{ fontSize: '0.6rem', color: colors.textTertiary }}>
                          {dt} <Box component="span" sx={{ color: counts.active > 0 ? subColor : colors.textTertiary, fontWeight: 600 }}>{counts.active}/{counts.total}</Box>
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      </Paper>

      {/* Device List */}
      <Paper sx={{ 
        flex: 1,
        minHeight: 0,
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <List sx={{ 
          p: 1, 
          overflow: 'auto',
          flex: 1, // Use flex instead of fixed height
          minHeight: 0, // Important for flexbox scrolling
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: 4,
          },
        }}>
          {sortedDevices.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <DevicesIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 1 }} />
              <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                No devices in view
              </Typography>
            </Box>
          ) : (
            pagedDevices.map((device) => {
              const isSelected = selectedDevice?.device_id === device.device_id;
              const isFollowing = followedDevice?.device_id === device.device_id;
              const isCotravelerFollowed = !!cotravelerFollowedIds?.has(device.device_id);
              const elapsedSeconds = getElapsedSeconds(device);
              const elapsedTime = formatElapsedTime(device);
              const bgColor = getEntityColor(device);
              const typeInfo = getDeviceTypeInfo(device);
              const label = getDeviceLabel(device);
              const googleUrl = getGoogleStreetViewUrl(device.latitude, device.longitude);

              // Freshness color (used for both the inline label and the
              // left-edge dot when not selected). Maps to the same three
              // bands the previous Chip used (<1m / <1h / older).
              const freshnessColor = elapsedSeconds < 60
                ? '#4caf50'
                : elapsedSeconds < 3600
                  ? '#ff9800'
                  : '#f44336';

              return (
                <ListItem key={device.device_id} disablePadding sx={{ mb: 0.75 }}>
                  <ListItemButton
                    onClick={() => onDeviceSelect(device)}
                    sx={{
                      borderRadius: 1.5,
                      // Use a left-edge color stripe to encode device type
                      // instead of a giant filled identity pill. Selection
                      // and follow state stack via a thicker accented border
                      // on top of (or replacing) the stripe.
                      borderLeft: `4px solid ${bgColor}`,
                      borderTop: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      borderBottom: `1px solid ${colors.border}`,
                      ...(isFollowing && {
                        borderTop: `1px solid #2196f3`,
                        borderRight: `1px solid #2196f3`,
                        borderBottom: `1px solid #2196f3`,
                        borderLeft: `4px solid #2196f3`,
                      }),
                      ...(isSelected && !isFollowing && {
                        borderTop: `1px solid ${primaryColor}`,
                        borderRight: `1px solid ${primaryColor}`,
                        borderBottom: `1px solid ${primaryColor}`,
                      }),
                      background: isFollowing
                        ? isDark ? 'rgba(33, 150, 243, 0.10)' : 'rgba(33, 150, 243, 0.06)'
                        : isSelected
                          ? isDark ? `rgba(${primaryRgb}, 0.10)` : `rgba(${primaryRgb}, 0.05)`
                          : 'transparent',
                      transition: 'background 0.15s, transform 0.15s',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
                      },
                      py: 1,
                      pr: 1,
                      pl: 1.25,
                      ...(isFollowing && {
                        animation: 'pulse-border 2s ease-in-out infinite',
                        '@keyframes pulse-border': {
                          '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(33, 150, 243, 0.0)' },
                          '50%': { boxShadow: 'inset 0 0 0 1px rgba(33, 150, 243, 0.25)' },
                        },
                      }),
                      ...(isCotravelerFollowed && !isFollowing && {
                        borderTop: `1px solid ${COTRAVELER_PULSE_COLOR}`,
                        borderRight: `1px solid ${COTRAVELER_PULSE_COLOR}`,
                        borderBottom: `1px solid ${COTRAVELER_PULSE_COLOR}`,
                        animation: 'pulse-cotraveler 2s ease-in-out infinite',
                        '@keyframes pulse-cotraveler': {
                          '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(255, 23, 68, 0.0)' },
                          '50%': { boxShadow: `inset 0 0 0 1px ${COTRAVELER_TINT_ROW}` },
                        },
                      }),
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      {/* Header row: device-type icon + name, then quiet
                          action icons (visible on hover or when following). */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                        <Box sx={{ color: bgColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {typeInfo.icon}
                        </Box>
                        <Typography sx={{
                          color: colors.text,
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {label}
                        </Typography>
                        {isCotravelerFollowed && (
                          <Tooltip title="A live co-traveler is nearest this device" placement="top">
                            <Chip
                              label="FOLLOWED"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.58rem',
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                bgcolor: COTRAVELER_TINT_RING,
                                color: COTRAVELER_PULSE_COLOR,
                                border: '1px solid rgba(255, 23, 68, 0.55)',
                                '& .MuiChip-label': { px: 0.6 },
                              }}
                            />
                          </Tooltip>
                        )}
                        <Tooltip title={isFollowing ? 'Stop following' : 'Follow on map'} placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              (onFollowToggle || onDeviceSelect)(device);
                            }}
                            sx={{
                              p: 0.25,
                              color: isFollowing ? '#2196f3' : colors.textTertiary,
                              opacity: isFollowing ? 1 : 0.55,
                              transition: 'opacity 0.15s, color 0.15s',
                              '.MuiListItemButton-root:hover &': { opacity: 1 },
                              '&:hover': { color: '#2196f3' },
                              ...(isFollowing && {
                                animation: 'pulse 1.5s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 1 },
                                  '50%': { opacity: 0.55 },
                                },
                              }),
                            }}
                          >
                            <FollowIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {onShareToChat && (
                          <Tooltip title="Share to chat" placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onShareToChat(device);
                              }}
                              sx={{
                                p: 0.25,
                                color: colors.textTertiary,
                                opacity: 0,
                                transition: 'opacity 0.15s, color 0.15s',
                                '.MuiListItemButton-root:hover &': { opacity: 1 },
                                '&:hover': { color: primaryColor },
                              }}
                            >
                              <ShareIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Single metadata row: freshness dot + elapsed, then
                          battery, speed, heading and (aircraft only) altitude.
                          Dots/icons sit inline so the row reads as a sentence
                          instead of a chip rack. */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexWrap: 'wrap',
                        color: colors.textSecondary,
                        fontSize: '0.72rem',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: freshnessColor,
                            flexShrink: 0,
                          }} />
                          <Typography sx={{ color: freshnessColor, fontSize: '0.72rem', fontWeight: 500 }}>
                            {elapsedTime}
                          </Typography>
                        </Box>
                        {device.battery_level != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <BatteryIcon sx={{ fontSize: 13, color: device.battery_level > 20 ? '#4caf50' : '#f44336' }} />
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.72rem' }}>
                              {device.battery_level}%
                            </Typography>
                          </Box>
                        )}
                        {device.speed != null && device.speed > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <SpeedIcon sx={{ fontSize: 13, color: '#64b5f6' }} />
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.72rem' }}>
                              {formatSpeedMph(device.speed)}
                            </Typography>
                          </Box>
                        )}
                        {/* Heading is only meaningful while moving: a stationary
                            GPS reports whatever course it last had. */}
                        {device.bearing != null && (device.speed ?? 0) > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <NavigationIcon
                              sx={{
                                fontSize: 12,
                                color: '#64b5f6',
                                transform: `rotate(${device.bearing}deg)`,
                              }}
                            />
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.72rem' }}>
                              {formatBearing(device.bearing)}
                            </Typography>
                          </Box>
                        )}
                        {device.altitude_agl_m != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <HeightIcon sx={{ fontSize: 13, color: COP_DEVICE_COLORS.drone }} />
                            <Typography sx={{ color: colors.textSecondary, fontSize: '0.72rem' }}>
                              {Math.round(device.altitude_agl_m)} m AGL
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Footer: precise timestamp + Street View link. */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.25,
                      }}>
                        <Typography sx={{ color: colors.textTertiary, fontSize: '0.68rem' }}>
                          {formatUtcInstantInTimeZone(device.timestamp, timeZone || getBrowserTimeZone(), {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </Typography>
                        <Link
                          href={googleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#2196f3',
                            fontSize: '0.68rem',
                            textDecoration: 'none',
                            opacity: 0.75,
                            transition: 'opacity 0.15s',
                            '&:hover': { textDecoration: 'underline', opacity: 1 },
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Street View →
                        </Link>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
        {pageCount > 0 && (
          <Box sx={{ borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={pageCount}
              page={safePage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Per page:"
              sx={{
                minHeight: 40,
                color: colors.textSecondary,
                '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1, pr: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.65rem',
                },
                '& .MuiTablePagination-select': { fontSize: '0.7rem' },
                '& .MuiIconButton-root': { color: colors.textSecondary },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DeviceListPanel;
